<script setup>
import PortalWrapper from '@/components/portal/PortalWrapper.vue'
import PortalForm from '@/components/portal/PortalForm.vue'
import FormField from '@/components/shared/form/FormField.vue'
import TextInput from '@/components/shared/form/TextInput.vue'
import LanguageSelectInput from '@/components/shared/form/LanguageSelectInput.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import ActionButton from '@/components/shared/buttons/ActionButton.vue'
import Alert from '@/components/shared/core/Alert.vue'
import {useCurrentUser} from '@/composables/use-current-user.js'
import {useDynamicCohortEnrollment} from '@/composables/use-dynamic-cohort-enrollment.js'
import {useEnrollmentsQuerying} from '@/composables/use-enrollments-querying.js'
import {useDelay} from '@/composables/use-delay.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useAuthentication} from '@/composables/use-authentication.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useEmployeePortal} from '@/composables/use-employee-portal.js'
import {languageOptions, coatsLanguageOptions} from '@/constants/languages.js'
import {computed, ref} from 'vue'

//General composables
const {user, updateUser, hasLocale} = useCurrentUser()
const {processError} = useErrorHandling()
const {goToHome} = useNavigation()
const {delay} = useDelay()
const {redirectToLogout} = useAuthentication()
const {isCoats} = useEmployeePortal()

//Dynamic cohort enrollment management
const {
  kickOffEnrollment,
  startPolling,
  isErrorStatus,
} = useDynamicCohortEnrollment()

//Enrollment querying
const {
  enrollments,
  loadEnrollments,
  changeLimit,
} = useEnrollmentsQuerying()

//Change limit (without reload), as we just need one enrollment
changeLimit(1, false)

//Maximum number of attempts to wait for active enrollments (1 attempt per second)
const maxWaitAttempts = 20

//Helper to wait up to 7 seconds for the user to have any active enrollments
//The default filter already checks by active state, so we don't need to handle that here
const waitForActiveEnrollments = async (attempt = 1) => {

  //Bail if max attempts reached, no need to error
  if (attempt > maxWaitAttempts) {
    return
  }

  //Wait 1 second before the first query
  try {
    await delay(1000)
    await loadEnrollments()
    if (enrollments.value.length === 0) {
      return waitForActiveEnrollments(attempt + 1)
    }
  }
  catch (error) {
    processError(error)
  }
}

//Refs
const isRunningFlow = ref(false)
const flowStep = ref('')
const hasError = ref(false)

//Track and set language
const language = ref(null)
const setLanguage = value => language.value = value

//Limit the language options for Coats, otherwise use the full list
const availableLanguageOptions = computed(() => {
  if (isCoats.value) {
    return coatsLanguageOptions
  }
  return languageOptions
})

//Wording for the flow status message
const flowStatusMessage = computed(() => {

  //Flow is not running
  if (!isRunningFlow.value) {
    return ''
  }

  //Wording depends on step
  if (flowStep.value === 'update_user') {
    return 'Saving your language preference...'
  }
  else if (flowStep.value === 'prepare_enrollments') {
    return 'Preparing your enrollments...'
  }
  else if (flowStep.value === 'wait_for_active_enrollments') {
    return 'Waiting for your enrollments to start...'
  }
  else if (flowStep.value === 'done') {
    return 'Redirecting you to your training...'
  }
  return 'Finalizing your account...'
})

//Run the flow
const runFlow = async () => {
  try {

    //Toggle flag
    isRunningFlow.value = true

    //Update the user's locale
    flowStep.value = 'update_user'
    await updateUser({locale: language.value})

    //Kick off enrollments and start polling for the status
    flowStep.value = 'prepare_enrollments'
    await kickOffEnrollment()
    await startPolling()

    //Fail if enrollment failed
    if (isErrorStatus.value) {
      throw new Error('Error status')
    }

    //Wait for active enrollments
    flowStep.value = 'wait_for_active_enrollments'
    await waitForActiveEnrollments()

    //Go to home page
    flowStep.value = 'done'
    await goToHome()
  }
  catch (error) {
    processError(error)
    hasError.value = true
  }
  finally {
    isRunningFlow.value = false
    flowStep.value = ''
  }
}
</script>

<template>
  <PortalWrapper
    :heading="`Welcome ${user.displayName}`"
    :sub-heading="isRunningFlow ? flowStatusMessage : 'Please confirm your details to continue'"
  >
    <Spinner
      v-if="isRunningFlow"
      is-block
    />

    <PortalForm v-else>
      <Alert
        v-if="hasError"
        type="danger"
        text="Something went wrong setting up your account. Please try again"
        is-spaced
      />

      <FormField label="Employee ID">
        <TextInput
          :value="`${user.externalUserId ?? 'N/A'} (${user.displayName})`"
          is-readonly
          is-disabled
        />
      </FormField>

      <FormField
        v-if="!hasLocale"
        label="Preferred language"
        is-required
      >
        <LanguageSelectInput
          :value="language"
          :languages="availableLanguageOptions"
          @input="setLanguage($event.value)"
        />
      </FormField>

      <template #buttons>
        <ActionButton
          v-if="hasLocale"
          label="Continue to training"
          type="primary"
          is-block
          @click="goToHome()"
        />
        <ActionButton
          v-else
          label="Continue to training"
          type="primary"
          is-block
          :is-disabled="!language"
          @click="runFlow()"
        />
        <ActionButton
          label="This is not me"
          type="secondary"
          is-block
          @click="redirectToLogout()"
        />
      </template>
    </PortalForm>
  </PortalWrapper>
</template>
